import { createAsyncThunk } from '@reduxjs/toolkit';
import { endOfDay, getUnixTime, startOfDay } from 'date-fns';
import { EP_PERIODICITY_PERFORMANCES, EP_PERIODICITY_STATS } from "../../Api/endPoints";
import { getData } from "../../Api/api";
import {
    mappedSalesByDate,
    mappedSalesByHoursDay,
    mappedSalesByPartDay,
    mappedSalesByWeekDay,
    mappedTransactions
} from "./periodicity.mapper";
import { zonedTimeToUtc } from 'date-fns-tz';

export const getPeriodicityStats = createAsyncThunk(
    'periodicity/getPeriodicityStats',
    async ({ startDate, endDate, brand = 0 }, thunkAPI) => {
        const params = {
            brand_id: brand,
            start: getUnixTime(zonedTimeToUtc(startOfDay(startDate), "GMT")),
            end: getUnixTime(zonedTimeToUtc(endOfDay(endDate), "GMT")),
            timezone: new Date().getTimezoneOffset()
        }

        try {
            const {
                turnover_by_months: {
                    selected_period: turnoverByMonths,
                    last_symetric_period: prevTurnoverByMonths,
                    years: turnoverYears,
                },
                turnover_by_days: { selected_period: turnoverByDays },
                turnover_by_day_period: { selected_period: turnoverByDayPeriod },
                turnover_by_week_days: { selected_period: turnoverByWeekPeriod },
                turnover_by_hours: { selected_period: turnoverByHours },
            } = await getData(EP_PERIODICITY_STATS, { params });

            const { periodStatsData } = thunkAPI.getState().periodicity;

            return {
                salesGrowth: {
                    ...periodStatsData?.salesGrowth,
                    transactions: mappedTransactions(turnoverByMonths, startDate, turnoverYears),
                },
                salesGranularity: {
                    salesByDate: mappedSalesByDate(turnoverByDays, startDate),
                    salesByPartDay: mappedSalesByPartDay(turnoverByDayPeriod),
                    salesByWeekDay: mappedSalesByWeekDay(turnoverByWeekPeriod),
                    salesByHoursDay: mappedSalesByHoursDay(turnoverByHours, startDate)
                },
            }
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    },
)

export const getPeriodicityPerformances = createAsyncThunk(
    'periodicity/getPeriodicityPerforms',
    async ({ startDate, endDate, brand = 0 }, thunkAPI) => {
        const growthMsg = 'growth';
        const decreaseMsg = 'decrease';
        try {
            const params = {
                brand_id: brand,
                start: getUnixTime(zonedTimeToUtc(startOfDay(startDate), "GMT")),
                end: getUnixTime(zonedTimeToUtc(endOfDay(endDate), "GMT")),
                timezone: new Date().getTimezoneOffset()
            }
            const result = await getData(EP_PERIODICITY_PERFORMANCES, { params });

            const {
                last_symetric_period_sales: lastSymmetricPeriodSales,
                selected_period_sales: selectedPeriodSales,
            } = result;

            // Prevent division by zero
            let tmpSales = 0;
            if (selectedPeriodSales !== 0) {
                tmpSales = (
                    ((selectedPeriodSales - lastSymmetricPeriodSales) / selectedPeriodSales) *
                    100
                ).toFixed(2);
            }

            return {
                salesGrowth: {
                    ...thunkAPI.getState().periodicity?.periodStatsData?.salesGrowth,
                    sales: {
                        value: tmpSales > 0 ? tmpSales : tmpSales * -1,
                        label: tmpSales > 0 ? growthMsg : decreaseMsg,
                    },
                },
            }
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    },
)
